/**
 * Static vs dynamic step content (mirrors server/src/utils/stepContentMode.util.js;
 * the server re-validates every save).
 */
export const CONTENT_MODES = Object.freeze({ DYNAMIC: "dynamic", STATIC: "static" });

export const STATIC_CONTENT_LIMITS = Object.freeze({
  emailSubject: 200,
  email: 20000,
  linkedin: 8000,
  whatsapp: 4096,
});

const LINKEDIN_NON_MESSAGE_ACTIONS = new Set([
  "connect",
  "linkedin_connect",
  "linkedin_like",
  "like_post",
  "post_like",
  "like_latest_post",
]);

export const normalizeContentMode = (value) =>
  value === CONTENT_MODES.STATIC ? CONTENT_MODES.STATIC : CONTENT_MODES.DYNAMIC;

/** Steps that send a message body (connection invites go out without a note). */
export function supportsStaticContent(step) {
  const channel = String(step?.channel || "").toLowerCase();
  const action = String(step?.action || "").toLowerCase();
  if (channel === "email" || channel === "whatsapp") return true;
  if (channel === "linkedin") return !LINKEDIN_NON_MESSAGE_ACTIONS.has(action);
  return false;
}

export const isStaticContentStep = (step) =>
  normalizeContentMode(step?.content_mode ?? step?.step_content_mode) === CONTENT_MODES.STATIC &&
  supportsStaticContent(step);

/** The configured fixed message, from a step row or a timeline row joined with its step. */
export const getStaticStepContent = (step) => ({
  subject: String(step?.email_subject ?? step?.step_email_subject ?? "").trim(),
  content: String(step?.template_text ?? step?.step_template_text ?? "").trim(),
});

export function getStaticContentLimit(channel) {
  return STATIC_CONTENT_LIMITS[String(channel || "").toLowerCase()] ?? null;
}

/** Field labels per channel. */
export function getComposerCopy(channel) {
  return {
    hasSubject: String(channel || "").toLowerCase() === "email",
    subjectLabel: "Subject",
    subjectErrorLabel: "Email subject",
    subjectPlaceholder: "Email subject",
    subjectLimit: STATIC_CONTENT_LIMITS.emailSubject,
    bodyLabel: "Message",
    bodyPlaceholder: "Write the message every lead will receive",
  };
}

/** Merge fields a fixed message can use (filled per lead at send time; mirrors the server). */
export const TEMPLATE_VARIABLES = Object.freeze([
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "full_name", label: "Full name" },
  { key: "company", label: "Company" },
  { key: "title", label: "Title" },
  { key: "sender_name", label: "Your name" },
]);

const VARIABLE_KEYS = TEMPLATE_VARIABLES.map((variable) => variable.key);
const VARIABLE_PATTERN = /\{\{\s*([a-zA-Z_]+)\s*(?:\|([^}]*))?\}\}/g;

export function findUnknownTemplateVariables(text) {
  const unknown = new Set();
  for (const match of String(text ?? "").matchAll(VARIABLE_PATTERN)) {
    if (!VARIABLE_KEYS.includes(match[1].toLowerCase())) unknown.add(match[1]);
  }
  return [...unknown];
}

/** User-facing validation errors for a fixed message; empty when it can be saved. */
export function validateStaticStepContent({ channel, action, subject = "", content = "" }) {
  if (!supportsStaticContent({ channel, action })) {
    return [
      "Fixed messages are supported for email, LinkedIn message and WhatsApp steps only.",
    ];
  }
  const errors = [];
  const trimmedSubject = String(subject).trim();
  const trimmedContent = String(content).trim();
  const copy = getComposerCopy(channel);
  if (copy.hasSubject) {
    if (!trimmedSubject) errors.push(`${copy.subjectErrorLabel} is required.`);
    if (trimmedSubject.length > copy.subjectLimit) {
      errors.push(`${copy.subjectErrorLabel} must be ${copy.subjectLimit} characters or less.`);
    }
  }
  const unknown = findUnknownTemplateVariables(`${trimmedSubject}\n${trimmedContent}`);
  if (unknown.length) {
    errors.push(`Unknown variable ${unknown.map((name) => `{{${name}}}`).join(", ")}.`);
  }
  const limit = getStaticContentLimit(channel);
  if (!trimmedContent) errors.push(`${copy.bodyLabel} is required.`);
  if (limit && trimmedContent.length > limit) {
    errors.push(`${copy.bodyLabel} must be ${limit.toLocaleString("en-US")} characters or less.`);
  }
  return errors;
}

/** Campaign-level: whether per-lead AI content is written (mirrors the server). */
export const CONTENT_GENERATION = Object.freeze({
  AUTOMATIC: "automatic",
  OFF: "off",
});

/** AI-personalized content is on by default, and on always means automatic. */
export const DEFAULT_NEW_CAMPAIGN_CONTENT_GENERATION = CONTENT_GENERATION.AUTOMATIC;

export const getContentGeneration = (campaign) => {
  const value = campaign?.content_generation ?? campaign?.contentGeneration;
  return Object.values(CONTENT_GENERATION).includes(value) ? value : CONTENT_GENERATION.AUTOMATIC;
};

export const CONTENT_GENERATION_COPY = Object.freeze({
  automatic:
    "AI researches each lead's signals and writes their messages automatically when leads are added.",
  off: "No AI. Every message step sends a fixed message with each lead's details filled in.",
});

/** AI on means automatic: signals and messages are written without the user asking. */
export const nextContentGeneration = (aiOn) =>
  aiOn ? CONTENT_GENERATION.AUTOMATIC : CONTENT_GENERATION.OFF;

// Campaign-level templates (mirrors normalizeMessageTemplates on the server).
export const TEMPLATE_CHANNELS = Object.freeze([
  { key: "email", title: "Email" },
  { key: "linkedin", title: "LinkedIn" },
  { key: "whatsapp", title: "WhatsApp" },
]);

export const getTemplateChannelKey = (channel) => String(channel || "").toLowerCase();

/** Validation errors per channel key; empty object when everything can be saved. */
export function validateMessageTemplates(templates = {}) {
  const errors = {};
  for (const { key } of TEMPLATE_CHANNELS) {
    const entry = templates?.[key] || {};
    const copy = getComposerCopy(key);
    const body = String(entry.body ?? "").trim();
    const subject = copy.hasSubject ? String(entry.subject ?? "").trim() : "";
    if (!body && !subject) continue;
    const list = [];
    if (copy.hasSubject && !subject) list.push(`Add a ${copy.subjectLabel.toLowerCase()}.`);
    if (!body) list.push(`Add a ${copy.bodyLabel.toLowerCase()}.`);
    const limit = getStaticContentLimit(key);
    if (body.length > limit) list.push(`${copy.bodyLabel} must be ${limit.toLocaleString("en-US")} characters or less.`);
    if (subject.length > copy.subjectLimit) {
      list.push(`${copy.subjectLabel} must be ${copy.subjectLimit} characters or less.`);
    }
    const unknown = findUnknownTemplateVariables(`${subject}\n${body}`);
    if (unknown.length) list.push(`Unknown variable ${unknown.map((n) => `{{${n}}}`).join(", ")}.`);
    if (list.length) errors[key] = list;
  }
  return errors;
}

/** Only channels with content are sent to the API. */
export function cleanMessageTemplates(templates = {}) {
  const out = {};
  for (const { key } of TEMPLATE_CHANNELS) {
    const body = String(templates?.[key]?.body ?? "").trim();
    const subject = String(templates?.[key]?.subject ?? "").trim();
    if (!body && !subject) continue;
    out[key] = getComposerCopy(key).hasSubject ? { subject, body } : { body };
  }
  return out;
}

export const hasAnyMessageTemplate = (templates) =>
  Object.keys(cleanMessageTemplates(templates)).length > 0;
